import { FSRef, UsageReport } from '@sdk';
import { Trans, useLingui } from '@lingui/react/macro';
import { ReportAssetShell } from '@src/components/assets/editor/ReportAssetShell';
import { StatTile } from '@src/components/assets/editor/agent-trace/simple/SimpleSessionReport';
import { formatDuration, formatNumber } from '@src/components/lens-viewer/shared/format-utils';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { DockPointer } from '@src/navigation/DockPointer';
import { useUsageReportDoc, type UsageReportData, type UsageSessionRow } from './useUsageReportDoc';

interface UsageReportAssetEditorProps {
  fsRef: FSRef;
  report: UsageReport;
}

function BreakdownTable({ title, cols, rows }: { title: string; cols: [string, string]; rows: [string, string][] }) {
  if (!rows.length) return null;
  return (
    <div className="min-w-0 flex-1">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-start text-muted-foreground">
            <th className="font-medium">{cols[0]}</th>
            <th className="w-16 text-end font-medium">{cols[1]}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-t border-border/50">
              <td className="truncate py-0.5 pe-2 text-foreground" title={k}>
                {k}
              </td>
              <td className="py-0.5 text-end tabular-nums text-muted-foreground">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SessionsTable({ data }: { data: UsageReportData }) {
  const { navigation } = useDockNavigation();
  const rows = [...data.sessions].sort((a, b) => b.cost_usd - a.cost_usd);
  if (!rows.length) return null;

  const open = (s: UsageSessionRow) => {
    // URL-first drill-down into the raw transcript / call-stack lens.
    navigation.openDock(DockPointer.forLensTranscript('claude', s.session_id));
  };

  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Trans>Sessions ({rows.length})</Trans>
      </h3>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-start text-muted-foreground">
            <th className="font-medium">
              <Trans>session</Trans>
            </th>
            <th className="w-16 text-end font-medium">
              <Trans>cost</Trans>
            </th>
            <th className="w-16 text-end font-medium">
              <Trans>time</Trans>
            </th>
            <th className="w-14 text-end font-medium">
              <Trans>prompts</Trans>
            </th>
            <th className="w-12 text-end font-medium">
              <Trans>skills</Trans>
            </th>
            <th className="w-12 text-end font-medium">
              <Trans>agents</Trans>
            </th>
            <th className="w-12 text-end font-medium">
              <Trans>errors</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr
              key={s.session_id}
              className="cursor-pointer border-t border-border/50 hover:bg-muted/60"
              title={`Open transcript · ${s.project || s.cwd}`}
              onClick={() => open(s)}
            >
              <td className="truncate py-1 pe-2 text-primary hover:underline">{s.title || s.session_id.slice(0, 8)}</td>
              <td className="py-1 text-end tabular-nums text-muted-foreground">${s.cost_usd.toFixed(2)}</td>
              <td className="py-1 text-end tabular-nums text-muted-foreground">{formatDuration(s.duration_ms)}</td>
              <td className="py-1 text-end tabular-nums text-muted-foreground">{s.prompt_count}</td>
              <td className="py-1 text-end tabular-nums text-muted-foreground">{s.skills.length}</td>
              <td className="py-1 text-end tabular-nums text-muted-foreground">{s.agents.length}</td>
              <td className="py-1 text-end tabular-nums text-muted-foreground">{s.tool_failures || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * UsageReport viewer: headline stat tiles, token split + breakdown tables, then a
 * per-session drill-down table whose rows deep-link the raw transcript/call-stack
 * lens (URL-first via `navigation.openDock`).
 */
export function UsageReportAssetEditor({ fsRef, report }: UsageReportAssetEditorProps) {
  const { t } = useLingui();
  const { data, error, loading } = useUsageReportDoc(fsRef);

  return (
    <ReportAssetShell fsRef={fsRef} name={report.name} testId="usage-report-editor" loading={loading} error={error}>
      {data && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatTile label={t`cost`} value={`$${(data.total_cost_usd ?? 0).toFixed(2)}`} />
            <StatTile label={t`sessions`} value={String(data.session_count ?? 0)} />
            <StatTile label={t`active`} value={formatDuration(data.total_duration_ms)} />
            <StatTile label={t`tokens`} value={formatNumber(data.total_tokens)} />
          </div>

          <div className="flex flex-wrap gap-6">
            <BreakdownTable
              title={t`Tokens`}
              cols={[t`dimension`, t`tokens`]}
              rows={[
                [t`input`, formatNumber(data.input_tokens)],
                [t`output`, formatNumber(data.output_tokens)],
                [t`cache read`, formatNumber(data.cache_read_tokens)],
                [t`cache write`, formatNumber(data.cache_creation_tokens)],
                [t`cache hit rate`, `${Math.round((data.cache_hit_rate ?? 0) * 100)}%`],
              ]}
            />
            <BreakdownTable
              title={t`Top skills`}
              cols={[t`skill`, t`uses`]}
              rows={data.top_skills.map((s) => [s.name, String(s.count)])}
            />
            <BreakdownTable
              title={t`Agents`}
              cols={[t`agent`, t`spawns`]}
              rows={data.top_agents.map((a) => [a.type, String(a.count)])}
            />
            <BreakdownTable
              title={t`Top tools`}
              cols={[t`tool`, t`calls`]}
              rows={data.top_tools.map((t) => [t.name, String(t.count)])}
            />
            <BreakdownTable
              title={t`Models`}
              cols={[t`model`, t`cost`]}
              rows={data.models.map((m) => [m.model, `$${m.cost_usd.toFixed(2)}`])}
            />
          </div>

          {data.sample_prompts.length > 0 && (
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Trans>Sample prompts</Trans>
              </h3>
              <ul className="list-disc space-y-0.5 ps-4 text-xs text-muted-foreground">
                {data.sample_prompts.map((p, i) => (
                  <li key={i} className="truncate" title={p}>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <SessionsTable data={data} />
        </div>
      )}
    </ReportAssetShell>
  );
}
